// See: backend/docs/arvento-telemetry.md §11 + §21
// Ayarlar → Arvento. İki bağımsız kart:
//   1) Arvento Bağlantısı — bir kez kurulur, nadiren değişir: entegrasyon anahtarı (anında kaydedilir),
//      durum özeti, kimlik bilgileri MODAL'da (username + PIN1/PIN2; "PIN'leri göster" isteğe bağlı
//      GET /telemetry/config/credentials ile açık metin getirir), "Bağlantıyı test et".
//   2) Servis konum senkronu — operasyonel ayarlar, kendi Kaydet düğmesiyle.
// PIN'ler /config yanıtında yer almaz (hasPin1/hasPin2); yalnızca kullanıcı açıkça isteyince çekilir.

import React, { useContext, useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";

import Grid from "@mui/material/Grid";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { useHttpClient } from "shared/hooks/http-hook";
import { AuthContext } from "shared/context/auth-context";
import Input from "shared/components/FormElements/Input";
import Button from "shared/components/FormElements/Button";
import LoadingSpinner from "shared/components/UIElements/LoadingSpinner";
import Modal from "shared/components/UIElements/Modal";
import { VALIDATOR_NONE } from "shared/util/validators";

import "./TelemetrySettings.css";

const API = () => process.env.REACT_APP_BACKEND_URL;
const JSON_HEADERS = { "Content-Type": "application/json" };
const DEFAULT_MARGIN = 30;

const TelemetrySettings = () => {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const { isLoading, sendRequest } = useHttpClient();

  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);

  // Kart 1 — bağlantı
  const [enabled, setEnabled] = useState(false);
  const [isTogglingEnabled, setIsTogglingEnabled] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);

  // Kimlik bilgileri modalı
  const [credOpen, setCredOpen] = useState(false);
  const [credUsername, setCredUsername] = useState("");
  const [credPin1, setCredPin1] = useState("");
  const [credPin2, setCredPin2] = useState("");
  const [revealed, setRevealed] = useState(null); // { username, pin1, pin2 } | null
  const [isRevealing, setIsRevealing] = useState(false);
  const [isSavingCred, setIsSavingCred] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [testResult, setTestResult] = useState(null); // { ok, text }

  // Kart 2 — servis konum senkronu
  const [stEnabled, setStEnabled] = useState(false);
  const [stAutoStart, setStAutoStart] = useState(true);
  const [stMargin, setStMargin] = useState(DEFAULT_MARGIN);
  const [isSavingSt, setIsSavingSt] = useState(false);

  const applyConfig = useCallback((data) => {
    setConfig(data);
    setEnabled(!!data.enabled);
    const st = data.serviceTracking || {};
    setStEnabled(!!st.enabled);
    setStAutoStart(st.autoStart !== false);
    const m = Number(st.windowMarginMinutes);
    setStMargin(Number.isFinite(m) ? m : DEFAULT_MARGIN);
  }, []);

  const fetchConfig = useCallback(async () => {
    try {
      const data = await sendRequest(`${API()}/telemetry/config`);
      applyConfig(data);
    } catch (err) {
      window?.toast?.error?.(err?.message || t("actionFailed"));
    }
  }, [sendRequest, t, applyConfig]);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await sendRequest(`${API()}/telemetry/status`);
      setStatus(data);
    } catch (err) {
      // status opsiyonel, sessizce geç
    }
  }, [sendRequest]);

  useEffect(() => {
    fetchConfig();
    fetchStatus();
  }, [fetchConfig, fetchStatus]);

  const patchConfig = useCallback(
    async (payload) => {
      const data = await sendRequest(`${API()}/telemetry/config`, "PATCH", JSON.stringify(payload), JSON_HEADERS);
      if (data?.config) applyConfig({ ...(config || {}), ...data.config });
      return data;
    },
    [sendRequest, applyConfig, config],
  );

  const refreshAuth = useCallback(async () => {
    if (auth?.login && auth?.id && auth?.token) {
      await auth.login(auth.id, auth.token);
    }
  }, [auth]);

  // --- Kart 1: entegrasyon anahtarı anında kaydedilir ---
  const onToggleEnabled = async (next) => {
    const prev = enabled;
    setEnabled(next);
    setIsTogglingEnabled(true);
    try {
      await patchConfig({ enabled: next });
      window?.toast?.success?.(t("telemetryIntegrationSaved"));
      await fetchStatus();
      await refreshAuth();
    } catch (err) {
      setEnabled(prev);
      window?.toast?.error?.(err?.message || t("actionFailed"));
    } finally {
      setIsTogglingEnabled(false);
    }
  };

  const runSync = async () => {
    const data = await sendRequest(`${API()}/telemetry/sync/now`, "POST", JSON.stringify({}), JSON_HEADERS);
    await fetchStatus();
    return data;
  };

  const onSyncNow = async () => {
    setIsSyncing(true);
    try {
      const data = await runSync();
      if (data?.stats?.error) window?.toast?.error?.(data.stats.error);
      else if (data?.stats?.skippedReason) window?.toast?.info?.(data.stats.skippedReason);
      else window?.toast?.success?.(`${data?.stats?.matched ?? 0} / ${data?.stats?.fetched ?? 0}`);
    } catch (err) {
      window?.toast?.error?.(err?.message || t("actionFailed"));
    } finally {
      setIsSyncing(false);
    }
  };

  // --- Kimlik bilgileri modalı ---
  const openCredentials = () => {
    setCredUsername(config?.username || "");
    setCredPin1("");
    setCredPin2("");
    setRevealed(null);
    setTestResult(null);
    setCredOpen(true);
  };

  const closeCredentials = () => {
    setCredOpen(false);
    setRevealed(null);
  };

  const onToggleReveal = async () => {
    if (revealed) {
      setRevealed(null);
      return;
    }
    setIsRevealing(true);
    try {
      const data = await sendRequest(`${API()}/telemetry/config/credentials`);
      setRevealed(data || {});
    } catch (err) {
      window?.toast?.error?.(err?.message || t("actionFailed"));
    } finally {
      setIsRevealing(false);
    }
  };

  const onSaveCredentials = async (e) => {
    e?.preventDefault?.();
    setIsSavingCred(true);
    try {
      const payload = { username: (credUsername || "").trim() };
      if (credPin1) payload.pin1 = credPin1;
      if (credPin2) payload.pin2 = credPin2;
      await patchConfig(payload);
      window?.toast?.success?.(t("telemetryCredentialsSaved"));
      setCredPin1("");
      setCredPin2("");
      setRevealed(null);
      await fetchConfig();
      await fetchStatus();
      await refreshAuth();
      setCredOpen(false);
    } catch (err) {
      window?.toast?.error?.(err?.message || t("actionFailed"));
    } finally {
      setIsSavingCred(false);
    }
  };

  const onTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const data = await runSync();
      if (data?.stats?.error) {
        setTestResult({ ok: false, text: `${t("telemetryTestFail")}: ${data.stats.error}` });
      } else if (data?.stats?.skippedReason) {
        setTestResult({ ok: false, text: data.stats.skippedReason });
      } else {
        setTestResult({
          ok: true,
          text: t("telemetryTestOk", { matched: data?.stats?.matched ?? 0, fetched: data?.stats?.fetched ?? 0 }),
        });
      }
    } catch (err) {
      setTestResult({ ok: false, text: `${t("telemetryTestFail")}: ${err?.message || ""}` });
    } finally {
      setIsTesting(false);
    }
  };

  // --- Kart 2: servis konum senkronu ---
  const onSaveServiceTracking = async (e) => {
    e?.preventDefault?.();
    setIsSavingSt(true);
    try {
      const marginNum = Number(stMargin);
      await patchConfig({
        serviceTracking: {
          enabled: stEnabled,
          autoStart: stAutoStart,
          windowMarginMinutes: Number.isInteger(marginNum) ? marginNum : DEFAULT_MARGIN,
        },
      });
      window?.toast?.success?.(t("telemetryServiceTrackingSaved"));
      await fetchConfig();
    } catch (err) {
      window?.toast?.error?.(err?.message || t("actionFailed"));
    } finally {
      setIsSavingSt(false);
    }
  };

  if (isLoading && !config) {
    return <LoadingSpinner />;
  }

  const hasCredentials = !!(config?.username && config?.hasPin1 && config?.hasPin2);
  const isConnected = !!(status?.enabled && status?.hasCredentials);
  const globallyDisabled = status?.globallyEnabled === false;

  const storedLine = (revealedValue, has) => {
    if (revealed) return `${t("telemetryStoredValue")}: ${revealedValue || t("telemetryNotSet")}`;
    return `${t("telemetryStoredValue")}: ${has ? "••••••••" : t("telemetryNotSet")}`;
  };

  return (
    <div className="centered-form-padding-lg telemetry-settings">
      <div className="telemetry-page-header">
        <h2 className="telemetry-page-title">{t("arventoIntegration")}</h2>
        <p className="telemetry-page-subtitle">{t("telemetryEnabledDesc")}</p>
      </div>

      {globallyDisabled && <div className="telemetry-banner telemetry-banner--error">{t("telemetryGloballyDisabled")}</div>}

      <Grid container spacing={3} alignItems="stretch">
        {/* ---------------- Kart 1: Bağlantı ---------------- */}
        <Grid item xs={12} md={5}>
          <section className="telemetry-card">
            <h3 className="telemetry-card__title">{t("telemetryConnectionTitle")}</h3>
            <p className="telemetry-card__desc">{t("telemetryConnectionDesc")}</p>

            <FormControlLabel
              control={
                <Switch
                  checked={enabled}
                  disabled={isTogglingEnabled}
                  onChange={(e) => onToggleEnabled(e.target.checked)}
                  color="primary"
                />
              }
              label={t("arventoIntegrationEnabled")}
            />

            <dl className="telemetry-kv">
              <dt>{t("telemetryStatusLabel")}</dt>
              <dd>
                <span className={`telemetry-pill ${isConnected ? "telemetry-pill--ok" : "telemetry-pill--off"}`}>
                  {isConnected ? t("telemetryStatusConnected") : t("telemetryStatusDisconnected")}
                </span>
              </dd>

              <dt>{t("arventoUsername")}</dt>
              <dd>{config?.username || "—"}</dd>

              <dt>{t("telemetryCredentialsLabel")}</dt>
              <dd>
                <span className={`telemetry-pill ${hasCredentials ? "telemetry-pill--ok" : "telemetry-pill--warn"}`}>
                  {hasCredentials ? t("telemetryCredentialsSet") : t("telemetryCredentialsMissing")}
                </span>
              </dd>

              <dt>{t("telemetryLastSync")}</dt>
              <dd>{status?.lastSyncedAt ? new Date(status.lastSyncedAt).toLocaleString() : "—"}</dd>

              <dt>{t("telemetryDailyQuota")}</dt>
              <dd>
                {status?.quota?.dailyQuotaUsed ?? 0} / {status?.quota?.dailyQuota ?? "—"}
              </dd>
            </dl>

            {status?.lastSyncError ? <div className="telemetry-error-text">{status.lastSyncError}</div> : null}

            <div className="telemetry-actions">
              <Button type="button" onClick={openCredentials}>
                {t("telemetryEditCredentials")}
              </Button>
              <Button type="button" inverse disabled={isSyncing || !enabled || !hasCredentials} onClick={onSyncNow}>
                {isSyncing ? "…" : t("telemetrySyncNow")}
              </Button>
            </div>
          </section>
        </Grid>

        {/* ---------------- Kart 2: Servis konum senkronu ---------------- */}
        <Grid item xs={12} md={7}>
          <form className="telemetry-card" onSubmit={onSaveServiceTracking}>
            <h3 className="telemetry-card__title">{t("telemetryServiceTrackingTitle")}</h3>
            <p className="telemetry-card__desc">{t("telemetryServiceTrackingDesc")}</p>

            {!enabled && <div className="telemetry-banner telemetry-banner--info">{t("telemetryServiceTrackingRequiresIntegration")}</div>}

            <FormControlLabel
              control={
                <Switch
                  checked={stEnabled}
                  onChange={(e) => setStEnabled(e.target.checked)}
                  color="primary"
                  disabled={!enabled}
                />
              }
              label={t("telemetryServiceTrackingEnabled")}
            />

            <div className="telemetry-field">
              <FormControlLabel
                control={
                  <Switch
                    checked={stAutoStart}
                    onChange={(e) => setStAutoStart(e.target.checked)}
                    color="primary"
                    disabled={!enabled || !stEnabled}
                  />
                }
                label={t("telemetryServiceTrackingAutoStart")}
              />
              <p className="telemetry-hint">{t("telemetryServiceTrackingAutoStartDesc")}</p>
            </div>

            <div className="telemetry-field telemetry-field--narrow">
              <label htmlFor="arvento-service-tracking-margin" className="telemetry-label">
                {t("telemetryServiceTrackingMargin")}
              </label>
              <input
                id="arvento-service-tracking-margin"
                className="telemetry-number"
                type="number"
                min={0}
                max={180}
                step={5}
                value={stMargin}
                disabled={!enabled || !stEnabled}
                onChange={(e) => setStMargin(e.target.value === "" ? "" : Number(e.target.value))}
              />
              <p className="telemetry-hint">{t("telemetryServiceTrackingMarginDesc")}</p>
            </div>

            <div className="telemetry-actions">
              <Button type="submit" disabled={isSavingSt || !enabled}>
                {isSavingSt ? "…" : t("save")}
              </Button>
            </div>
          </form>
        </Grid>
      </Grid>

      {/* ---------------- Kimlik bilgileri modalı ---------------- */}
      <Modal
        show={credOpen}
        onCancel={closeCredentials}
        onClose
        header={t("telemetryCredentialsModalTitle")}
        footer={
          <div className="telemetry-actions telemetry-actions--end">
            <Button type="button" inverse onClick={closeCredentials} disabled={isSavingCred}>
              {t("cancel")}
            </Button>
            <Button type="button" inverse onClick={onTestConnection} disabled={isTesting || !enabled || !hasCredentials}>
              {isTesting ? "…" : t("telemetryTestConnection")}
            </Button>
            <Button type="button" onClick={onSaveCredentials} disabled={isSavingCred}>
              {isSavingCred ? "…" : t("save")}
            </Button>
          </div>
        }
      >
        {credOpen && (
          <div className="telemetry-modal__body">
            <p className="telemetry-card__desc">{t("telemetryCredentialsDesc")}</p>

            <Input
              id="arvento-username"
              element="input"
              type="text"
              label={t("arventoUsername")}
              validators={[VALIDATOR_NONE()]}
              initialValue={credUsername}
              initialValid={true}
              name="arvento-username"
              autoComplete="off"
              onInput={(_, value) => setCredUsername(value)}
            />

            <div className="telemetry-reveal-row">
              <button type="button" className="telemetry-link-button" onClick={onToggleReveal} disabled={isRevealing}>
                {revealed ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                <span>{isRevealing ? "…" : revealed ? t("telemetryHidePins") : t("telemetryShowPins")}</span>
              </button>
            </div>

            <Input
              id="arvento-pin1"
              element="input"
              type={revealed ? "text" : "password"}
              label={t("arventoPin1")}
              validators={[VALIDATOR_NONE()]}
              initialValue={""}
              initialValid={true}
              name="arvento-pin1"
              autoComplete="new-password"
              placeholder={config?.hasPin1 ? t("arventoPinPlaceholder") : ""}
              onInput={(_, value) => setCredPin1(value)}
            />
            <p className="telemetry-hint telemetry-hint--mono">{storedLine(revealed?.pin1, config?.hasPin1)}</p>

            <Input
              id="arvento-pin2"
              element="input"
              type={revealed ? "text" : "password"}
              label={t("arventoPin2")}
              validators={[VALIDATOR_NONE()]}
              initialValue={""}
              initialValid={true}
              name="arvento-pin2"
              autoComplete="new-password"
              placeholder={config?.hasPin2 ? t("arventoPinPlaceholder") : ""}
              onInput={(_, value) => setCredPin2(value)}
            />
            <p className="telemetry-hint telemetry-hint--mono">{storedLine(revealed?.pin2, config?.hasPin2)}</p>

            {testResult && (
              <div className={`telemetry-banner ${testResult.ok ? "telemetry-banner--ok" : "telemetry-banner--error"}`}>
                {testResult.text}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default TelemetrySettings;
